import type { ReactNode } from 'react';
import { Button, Card, Collapse, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

export interface KeywordDetailedFilterBarProps {
  keywordId: string;
  keyword: string;
  onKeywordChange: (value: string) => void;
  keywordPlaceholder: string;
  detailedOpen: boolean;
  detailedCount: number;
  onToggleDetailed: () => void;
  onClear: () => void;
  /** 使用履歴など、スクロール中もバーを残す */
  sticky?: boolean;
  /** 基準日など、検索欄の左側 */
  leading?: ReactNode;
  /** 件数バッジ・展開ボタンなど、詳細トグル右側 */
  trailing?: ReactNode;
  /**
   * 検索欄と詳細トグルのあいだ（一覧の統計ストリップ等）。
   * 表示制御（d-md-flex 等）は呼び出し側。
   */
  mid?: ReactNode;
  /** カード先頭（管理者トグルなど） */
  toolbar?: ReactNode;
  /** Collapse 内の詳細フィールド */
  detailedPanel: ReactNode;
  /** ラベル行を省略して縦を詰める（サンプル一覧向け） */
  hideKeywordLabel?: boolean;
  /** 検索入力の表示ラベル（未指定時は aria → reports.filter_keyword） */
  keywordLabel?: string;
  /** 検索入力の aria-label（未指定時は keywordLabel → reports.filter_keyword） */
  keywordAriaLabel?: string;
  /** 検索入力の title（短プレースホルダの補足。未指定時はプレースホルダ） */
  keywordTitle?: string;
  /**
   * plain: 外側 Card をやめ、ネスト画面（ケース詳細など）の縦余白を減らす
   */
  variant?: 'card' | 'plain';
}

/**
 * 帳票／使用履歴／サンプル一覧で共有する「検索 + クリア + 詳細フィルター」バー。
 */
export default function KeywordDetailedFilterBar({
  keywordId,
  keyword,
  onKeywordChange,
  keywordPlaceholder,
  detailedOpen,
  detailedCount,
  onToggleDetailed,
  onClear,
  sticky = false,
  leading,
  mid,
  trailing,
  toolbar,
  detailedPanel,
  hideKeywordLabel = false,
  keywordLabel,
  keywordAriaLabel,
  keywordTitle,
  variant = 'card',
}: KeywordDetailedFilterBarProps) {
  const { t } = useTranslation();
  const clearDisabled = !keyword && detailedCount === 0;
  const defaultSearchLabel = t('reports.filter_keyword', { defaultValue: '帳票検索' });
  const labelText = keywordLabel ?? keywordAriaLabel ?? defaultSearchLabel;
  const ariaLabel = keywordAriaLabel ?? labelText;
  const inputTitle = keywordTitle || keywordPlaceholder;
  const plain = variant === 'plain';
  /** サンプル一覧など: 折り返さず検索欄を優先（md 未満で操作が下段に落ちるのを防ぐ） */
  const tightBar = hideKeywordLabel;
  // 詳細トグル: visible / aria = short、title = full
  const filterShort = t('common.filter');
  const filterFull = t('sample_filter.detailed_filter');
  const filterCountSuffix = detailedCount > 0 ? ` (${detailedCount})` : '';

  const barBody = (
    <>
      {toolbar ? <div className="mb-2">{toolbar}</div> : null}
      <div
        className={`d-flex gap-2 keyword-detailed-filter-row ${
          hideKeywordLabel ? 'align-items-center' : 'align-items-end'
        } ${
          leading && tightBar
            ? 'flex-nowrap keyword-detailed-filter-row--with-leading'
            : leading
              ? 'flex-wrap'
              : tightBar
                ? 'flex-nowrap'
                : 'flex-wrap flex-xl-nowrap'
        }`}
      >
        {leading}
        <div
          className="flex-grow-1 sample-keyword-field min-w-0"
          style={{
            minWidth: leading ? (tightBar ? '6rem' : '10rem') : tightBar ? 0 : '14rem',
            flexBasis: leading ? (tightBar ? '6rem' : undefined) : tightBar ? '6rem' : '14rem',
          }}
        >
          {!hideKeywordLabel ? (
            <Form.Label htmlFor={keywordId} className="small text-muted mb-1">
              {labelText}
            </Form.Label>
          ) : null}
          <div className="input-group input-group-sm">
            <span className="input-group-text bg-white text-muted" aria-hidden="true">
              <i className="fas fa-search" />
            </span>
            <Form.Control
              id={keywordId}
              name="filterKeyword"
              type="search"
              placeholder={keywordPlaceholder}
              aria-label={ariaLabel}
              title={inputTitle}
              value={keyword}
              onChange={(e) => onKeywordChange(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              autoCorrect="off"
              autoCapitalize="off"
              className={clearDisabled ? undefined : 'sample-keyword-has-clear'}
            />
          </div>
          {/* input-group 外に置き、入力の上に重ねる（group 内だと form-control がクリックを奪う） */}
          <Button
            variant="link"
            size="sm"
            className={`sample-keyword-clear-inplace position-absolute end-0 text-muted${clearDisabled ? ' is-idle' : ''}`}
            disabled={clearDisabled}
            onClick={onClear}
            title={t('sample.clear_filters')}
            aria-label={t('common.clear')}
            tabIndex={clearDisabled ? -1 : 0}
            aria-hidden={clearDisabled}
          >
            <i className="fas fa-times" aria-hidden="true" />
          </Button>
        </div>
        {mid}
        <div className="d-flex align-items-center gap-1 gap-sm-2 flex-shrink-0 ms-xl-0">
          <Button
            variant={detailedOpen ? 'secondary' : 'outline-secondary'}
            size="sm"
            className="w-auto"
            aria-expanded={detailedOpen}
            onClick={onToggleDetailed}
            title={filterFull}
            aria-label={`${filterShort}${filterCountSuffix}`}
          >
            <i className="fas fa-sliders-h" aria-hidden="true" />
            {/* xxl 未満はアイコンのみ — 検索欄が trailing より狭くなるのを防ぐ */}
            <span className="d-none d-xxl-inline ms-1" aria-hidden="true">
              {filterShort}
              {filterCountSuffix}
            </span>
            {detailedCount > 0 ? (
              <span className="d-xxl-none ms-1" aria-hidden="true">
                ({detailedCount})
              </span>
            ) : null}
          </Button>
          {trailing}
        </div>
      </div>
    </>
  );

  return (
    <div
      onKeyDown={(e) => {
        if (e.key !== 'Escape' || clearDisabled) return;
        e.preventDefault();
        onClear();
      }}
    >
      {plain ? (
        <div className="mb-1 keyword-detailed-filter-plain">{barBody}</div>
      ) : (
        <Card
          className={`mb-2${sticky ? ' shadow-sm' : ''}`}
          style={
            sticky
              ? { position: 'sticky', top: 0, zIndex: 5, background: '#fff' }
              : undefined
          }
        >
          <Card.Body className="py-2 px-3">{barBody}</Card.Body>
        </Card>
      )}

      <Collapse in={detailedOpen} mountOnEnter unmountOnExit>
        {plain ? (
          <div className="mb-2 border rounded px-2 py-1 bg-light sample-detailed-filter-collapse">
            {detailedPanel}
          </div>
        ) : (
          <Card className="mb-2">
            <Card.Body className="py-2">{detailedPanel}</Card.Body>
          </Card>
        )}
      </Collapse>
    </div>
  );
}
